// src/admin/hooks/useKitBatches.js
import { useState, useEffect, useCallback } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { API_URL } from '../../config/env';

/**
 * Hook for managing kit code batches
 */
export default function useKitBatches() {
  const isMounted = useIsMounted();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/kit_batches`);
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      setBatches(data);
      setError(null);
    } catch (err) {
      if (!isMounted.current) return;
      console.error('Error fetching kit batches:', err);
      setBatches([]);
      setError(err);
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  }, [isMounted]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const generateBatch = useCallback(
    async (count, kitId, name) => {
      const res = await fetchWithAuth(`${API_URL}/v1/admin/kit_batches/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          count,
          kit_id: kitId,
          name: name || undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || res.statusText);
      }

      const result = await res.json();
      await fetchBatches();
      return result;
    },
    [fetchBatches]
  );

  const exportBatchCsv = useCallback(async (batchNumber) => {
    const res = await fetchWithAuth(
      `${API_URL}/v1/admin/kit_batches/${batchNumber}/export.csv`
    );
    if (!res.ok) throw new Error(res.statusText);

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kit_codes_batch_${batchNumber}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    a.remove();
  }, []);

  return {
    batches,
    loading,
    error,
    refresh: fetchBatches,
    generateBatch,
    exportBatchCsv,
  };
}

/**
 * Hook for fetching kit codes within a specific batch (lazy loading)
 */
export function useKitBatchShortUrls(batchNumber) {
  const isMounted = useIsMounted();
  const [shortUrls, setShortUrls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);

  const fetchShortUrls = useCallback(async () => {
    if (!batchNumber) return;

    setLoading(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/kit_batches/${batchNumber}`
      );
      if (!res.ok) throw new Error(res.statusText);
      const data = await res.json();
      setShortUrls(data.short_urls || []);
      setLoaded(true);
      setError(null);
    } catch (err) {
      if (!isMounted.current) return;
      console.error('Error fetching kit batch short URLs:', err);
      setShortUrls([]);
      setError(err);
    } finally {
      if (!isMounted.current) return;
      setLoading(false);
    }
  }, [batchNumber, isMounted]);

  return {
    shortUrls,
    loading,
    loaded,
    error,
    fetchShortUrls,
    refresh: fetchShortUrls,
  };
}

